import { Flex } from '@chakra-ui/react';
import { HoverAbleContainerComp } from 'components/HoverableComponents';
import MeshDonutChart from 'components/graph/MeshDonutChart';
import {
  CompetencyNameCell,
  CompetencyProgressCell,
  getTargetRoleColor,
  ProgressPercentageCell,
  CompetencyPeriodProgressCell,
} from 'screens/Levelup/components/common';
import ProgressCell from 'screens/Levelup/components/managerTable/tableCellComponents/ProgressCell';
import { EmptyBar } from 'screens/analytics/commonComponents';
import { FlexContainer, StyledBox, StyledGrid, VerticallyAlignedFlexContainer } from 'styles/common';
import { baseTheme } from 'themes/clients/baseTheme';

import { LogoWithoutNameImage } from 'constants/icons';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

import {
  LevelUpAnalyticsCountChangeCell,
  LevelUpAnalyticsFunctionCell,
  LevelUpAnalyticsIndividualCell,
  LevelUpAnalyticsManagerCell,
  LevelUpRiskEstimationCountCellForAnalytics,
  RoleDepartmentCell,
} from '../commonComponents/cellComponents';
import { TargetRoleDonutChartHoverComp } from '../commonComponents/hoverComponents';

export const LEVEL_UP_ANALYTICS_META_CELL = {
  FUNCTION: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        header: '',
        id,
        cell: (props) => (
          <LevelUpAnalyticsFunctionCell
            isExpanded={props.row.getIsExpanded()}
            canExpand={props.row.getCanExpand()}
            onExpandClick={props.row.getToggleExpandedHandler()}
            row={props.getValue()}
          />
        ),
        width: 'auto',
        align: 'left',
      },
    ],
    align: 'left',
  }),
  COMPETENCY: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        header: '',
        id,
        cell: (props) => {
          const { competencyName, levelUpCompetencyId, competencyId } = props.getValue()?.value ?? {};
          return <CompetencyNameCell name={competencyName} isExpandable={false} />;
        },
        width: 'auto',
        align: 'left',
      },
    ],
    align: 'left',
  }),
  EMPLOYEE_NAME: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        header: '',
        id,
        cell: (props) => (
          <LevelUpAnalyticsManagerCell
            isExpanded={props.row.getIsExpanded()}
            canExpand={props.row.getCanExpand()}
            onExpandClick={props.row.getToggleExpandedHandler()}
            row={props.getValue()}
          />
        ),
        width: '25rem',
        align: 'left',
      },
    ],
    width: '25rem',
    align: 'left',
  }),
  INDIVIDUAL_EMPLOYEE: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        header: '',
        id,
        cell: (props) => <LevelUpAnalyticsIndividualCell row={props.getValue()} />,
        width: 'auto',
        align: 'left',
        marginLeft: 1.5,
      },
    ],
    align: 'left',
    marginLeft: 1.5,
  }),
  ROLE: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        header: '',
        id,
        cell: (props) => {
          const { name, role, department } = props.getValue()?.value ?? {};
          return <RoleDepartmentCell role={name} department={department} />;
        },
        width: 'auto',
        align: 'left',
      },
    ],
    align: 'left',
  }),
  DONUT_GRAPH: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        id,
        header: '',
        cell: (props) => {
          const data = props.getValue()?.value;
          const dataArray = Object.keys(data).map((k) => ({
            ...data[k],
            key: k,
            color: getTargetRoleColor(k),
          }));

          return (
            <FlexContainer style={{ justifyContent: 'center' }}>
              <HoverAbleContainerComp
                displayInfoComp={<TargetRoleDonutChartHoverComp data={dataArray} />}
                calibrateAble={false}
              >
                <StyledBox width="72px" display="flex" justifyContent="center">
                  <MeshDonutChart data={dataArray} />
                </StyledBox>
              </HoverAbleContainerComp>
            </FlexContainer>
          );
        },
        width: '10rem',
        // fixedWidth: '10rem',
        align: 'center',
      },
    ],
  }),
  PROGRESS_BAR: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        header: '',
        id,
        cell: (props) => {
          const { value } = props.getValue() ?? {};
          return (
            <CompetencyProgressCell percentage={value} color={baseTheme.colors.functional['91CA7D']} showPercentage />
          );
        },
        width: '20rem',
        align: 'left',
      },
    ],
    width: '20rem',
    // fixedWidth: '12rem',
    align: 'left',
  }),
  PROGRESS_INCREMENT: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        header: '',
        id,
        cell: (props) => {
          const { value } = props.getValue() ?? {};
          return (
            <CompetencyPeriodProgressCell
              percentage={value}
              color={baseTheme.colors.functional['91CA7D']}
              showPercentage
            />
          );
        },
        width: '20rem',
        align: 'left',
      },
    ],
    width: '20rem',
    // fixedWidth: '12rem',
    align: 'left',
  }),
  RISK_ESTIMATION_PERCENTAGE: ({ displayName, id }) => ({
    header: () => (
      <VerticallyAlignedFlexContainer>
        <img src={LogoWithoutNameImage} alt="Logo" style={{ width: '18px', height: '18px', marginRight: '0.5rem' }} />
        {displayName}
      </VerticallyAlignedFlexContainer>
    ),
    //   id: 'riskEstimationCount',
    accessorKey: 'riskEstimationInfo',
    columns: [
      {
        accessorFn: (row) => row[id],
        id,
        header: '',
        cell: (props) => {
          const riskEstimation = props.getValue()?.value;
          if (riskEstimation === null) {
            return (
              <StyledGrid container justify="center" alignItems="center">
                <EmptyBar />
              </StyledGrid>
            );
          }
          return (
            <StyledGrid container>
              <StyledGrid>
                <LevelUpRiskEstimationCountCellForAnalytics riskEstimation={riskEstimation} />
              </StyledGrid>
            </StyledGrid>
          );
        },
        width: '12rem',
        // fixedWidth: '12rem',
        align: 'center',
      },
    ],
  }),
  PROGRESS_PERCENTAGE_BAR: ({ displayName, id }) => ({
    header: displayName,
    columns: [
      {
        accessorFn: (row) => row[id],
        id,
        header: '',
        cell: (props) => {
          const progress = props.getValue()?.value;

          return <ProgressCell progress={progress} />;
        },
        width: '15rem',
        // fixedWidth: '15rem',
        align: 'center',
      },
    ],
    width: '12rem',
  }),
  PROGRESS_PERCENTAGE: ({ displayName, id }) => ({
    header: displayName,
    id: `progress_percentage_header_${id}`,
    columns: [
      {
        accessorFn: (row) => row[id],
        id: `progress_percentage_${id}`,
        header: '',
        cell: (props) => {
          const value = props.getValue()?.value;
          return <ProgressPercentageCell value={value} />;
        },
        width: 'auto',
        fixedWidth: '6rem',
        align: 'center',
      },
    ],
  }),
  PLAIN_TEXT: ({ displayName, id }) => ({
    header: displayName,
    id: `progress_percentage_header_${id}`,
    columns: [
      {
        accessorFn: (row) => row[id],
        id: `progress_percentage_${id}`,
        header: '',
        cell: (props) => {
          const value = props.getValue()?.value;
          return (
            <Flex style={{ justifyContent: 'center', width: '100%' }}>
              <TextBodyMedium>{value}</TextBodyMedium>
            </Flex>
          );
        },
        width: 'auto',
        fixedWidth: '6rem',
        align: 'center',
      },
    ],
  }),
  AGGREGATED_METRICS: ({ id, displayName }) => {
    const header1 = 'Praise';
    const header2 = 'Feedback';

    const value1 = 'PRAISE';
    const value2 = 'FEEDBACK';

    return {
      header: displayName,
      columns: [
        {
          header: header1,
          id: `added_${id}`,
          accessorFn: (row) => row[id],
          cell: (props) => {
            const { [value1]: value } = props.getValue()?.value ?? {};

            return <LevelUpAnalyticsCountChangeCell count={value} />;
          },
          width: '6rem',
          // fixedWidth: '6rem',
          align: 'center',
          borderLeft: true,
        },
        {
          header: header2,
          id: `completed_${id}`,
          accessorFn: (row) => row[id],
          cell: (props) => {
            const { [value2]: value } = props.getValue()?.value ?? {};

            return <LevelUpAnalyticsCountChangeCell count={value} />;
          },
          width: '6rem',
          // fixedWidth: '6rem',
          align: 'center',
          borderLeft: true,
        },
      ],
    };
  },
  subRow: ({ uuidType, parentDepth, levelsForWhichLineToBeDrawn, isParentLastRow, id, displayName }) => {
    switch (uuidType) {
      case 'FUNCTION':
        return {
          header: displayName,
          columns: [
            {
              accessorFn: (row) => row[id],
              header: '',
              id: 'function',
              cell: (props) => {
                const isLastRow = props?.row?.index === props?.table?.options?.data?.length - 1;
                return (
                  <LevelUpAnalyticsFunctionCell
                    isExpanded={props.row.getIsExpanded()}
                    canExpand={props.row.getCanExpand()}
                    onExpandClick={props.row.getToggleExpandedHandler()}
                    row={props.getValue()}
                    isSubRow
                    isLast={isLastRow}
                    parentDepth={parentDepth}
                    levelsForWhichLineToBeDrawn={levelsForWhichLineToBeDrawn}
                    isParentLastRow={isParentLastRow}
                  />
                );
              },
              width: 'auto',
              align: 'left',
            },
          ],
        };
      case 'EMPLOYEE_NAME':
        return {
          header: displayName,
          columns: [
            {
              accessorFn: (row) => row[id],
              header: '',
              id,
              cell: (props) => {
                const isLastRow = props?.row?.index === props?.table?.options?.data?.length - 1;
                return (
                  <LevelUpAnalyticsManagerCell
                    isExpanded={props.row.getIsExpanded()}
                    canExpand={props.row.getCanExpand()}
                    onExpandClick={props.row.getToggleExpandedHandler()}
                    row={props.getValue()}
                    isSubRow
                    isLast={isLastRow}
                    parentDepth={parentDepth}
                    levelsForWhichLineToBeDrawn={levelsForWhichLineToBeDrawn}
                    isParentLastRow={isParentLastRow}
                  />
                );
              },
              width: '25rem',
              align: 'left',
            },
          ],
          width: '20rem',
        };

      default:
        break;
    }
  },
};
